import { gsap } from "gsap";
import { Point, Sprite } from "pixi.js";

import { EventsManager, GameEvents } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import {
    fitDisplayObjectToWidth,
    generateScaledSprite,
} from "../../../../abstracts/utils/GraphicUtils";
import { getRandomBetween, getRandomElement } from "../../../../abstracts/utils/JSUtils";
import { playSound, stopSound } from "../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../Apocalypse";
import {
    ApocalypseFontsNames,
    ApocalypseSoundNames,
    ApocalypseSpineNames,
    fsMultiMove,
} from "../../../assetsLoader/ApocalypseAssetsNames";
import { getWinLevel, winLevels } from "../../../config/ApocalypseConfig";
import { ApocalypseCounter } from "../../../utils/ApocalypseCounter";
import { cloneSymbolValue } from "../../../utils/ApocalypseGraphicUtils";
import { ApocalypseFsContainer } from "./ApocalypseFsContainer";
import { ApocalypseFsLeftContainer } from "./ApocalypseFsLeftContainer";

export class ApocalypseFsMultiplierContainer extends ApocalypseFsLeftContainer {
    protected currentGameMode: AbstractGameMode;
    protected tvFlickerSpine: AbstractSpine;

    constructor(name: string = `ApocalypseFsMultiplier`) {
        super(name);

        this.currentGameMode = Apocalypse.model.data.mode;
    }

    protected override handleEvents() {
        EventsManager.on(GameEvents.UPDATE_GAME_MODE, (mode: AbstractGameMode) => {
            if (mode !== AbstractGameMode.NORMAL && this.currentGameMode !== mode) {
                this.addChild(this.tvFlickerSpine);
                this.showDefaultTvAnimation();
                this.currentGameMode = mode;
            }
        });
    }

    protected override initializeExtraBackgroundAndText() {}

    protected showDefaultTvAnimation(): void {
        this.tvFlickerSpine.x = 719;
        this.tvFlickerSpine.y = 89;

        this.tvFlickerSpine.reset();
        this.tvFlickerSpine.getSpineTween(`flicker${getRandomBetween(1, 3, true)}`, true);
    }

    protected initializeCounterBackground(): void {
        super.initializeCounterBackground();
        this.counterBackground.name = `fsMultiplierBg`;
    }

    protected initializeCounter(): void {
        this.tvFlickerSpine = new AbstractSpine(ApocalypseSpineNames.TV_FLICKER);
        this.tvFlickerSpine.name = `tvFlicker`;

        this.counter = new ApocalypseCounter({
            initialValue: 0,
            fontProps: {
                text: ``,
                style: {
                    fontName: ApocalypseFontsNames.APOCALYPSE_BLUE,
                    fontSize: 7,
                },
            },
            suffix: `x`,
            highlightColor: `white`,
            name: `fsMultiplierCounter`,
            onCounterStart: () => {
                this.tvFlickerSpine.x = 706;
                this.tvFlickerSpine.y = 64;
                this.tvFlickerSpine.reset();

                this.tvFlickerSpine.getSpineTween(`flicker`, true);

                playSound({
                    name: ApocalypseSoundNames.WIN_COUNTUP,
                    repeat: 100,
                });
            },
            onCounterUpdate: () => {
                fitDisplayObjectToWidth(this.counter, 92);
            },
            onCounterComplete: () => {
                stopSound({
                    name: ApocalypseSoundNames.WIN_COUNTUP,
                });
            },
            onHighlightUpdate: () => {
                fitDisplayObjectToWidth(this.counter, 92);
            },
            onHighlightStart: () => {
                stopSound({
                    name: ApocalypseSoundNames.WIN_COUNTUP,
                });
                playSound({
                    name: ApocalypseSoundNames.FS_WIN_MID,
                });
            },
            onHighlightComplete: () => {
                this.showDefaultTvAnimation();
            },
        });

        fitDisplayObjectToWidth(this.counter, 92);

        this.counter.x = 710;
        this.counter.y = 56;
        this.counter.rotation = 6 * (Math.PI / 180);

        this.addChild(this.counter);
    }

    protected getFsBackground(): Sprite {
        const fsBackground = generateScaledSprite(`apocalypse/fsMultiplierBackground`);
        fsBackground.name = `fsMultiplierBackground`;
        fsBackground.x = 645; //! check mobile
        fsBackground.y = -34;

        return fsBackground;
    }

    private getShowValueAnimation(valueText: AbstractBitmapText): gsap.core.Tween {
        return gsap.to(valueText, {
            alpha: 1,
            duration: 0.3,
            y: valueText.y - 20,
        });
    }

    private getHideValueAnimation(valueText: AbstractBitmapText): gsap.core.Tween {
        return gsap.to(valueText, {
            alpha: 0,
            duration: 0.2,
        });
    }

    private moveValueToMultiplierAnimation(valueText: AbstractBitmapText): gsap.core.Tween {
        const globalPosition = this.counter.getGlobalPosition();
        const localPosition = this.parent.toLocal(globalPosition);

        return gsap.to(valueText, {
            onStart: () => {
                this.parent.addChild(valueText);
                playSound({
                    name: getRandomElement(fsMultiMove),
                });
            },
            x: localPosition.x,
            y: localPosition.y,
            duration: 0.3,
        });
    }

    public getDefaultMultiplierAnimation(
        symbolGlobalPosition: Point,
        newValue: number,
        finalValue: number,
        isM1: boolean = false
    ): gsap.core.Timeline {
        const valueText = cloneSymbolValue(
            symbolGlobalPosition,
            newValue,
            this.parent as ApocalypseFsContainer
        );

        this.parent.addChild(valueText);
        this.addChild(this.tvFlickerSpine);

        const timeline = gsap.timeline({
            onComplete: () => {
                valueText.destroy();
            },
        });

        timeline.add(this.getShowValueAnimation(valueText), 0);

        timeline.add(this.moveValueToMultiplierAnimation(valueText));

        timeline.add(this.getHideValueAnimation(valueText), `>-=0.1`);

        const winLevel = getWinLevel(newValue);
        const animationDuration = Math.min(winLevel.animationDuration / 6, 1);

        const maxWinLevel = winLevels[9];
        if (finalValue > maxWinLevel.min) {
            finalValue = maxWinLevel.min;
        }

        if (!isM1) {
            timeline.add(
                this.counter.getUpdateCounterTextAnimation(
                    finalValue,
                    false,
                    true,
                    animationDuration
                )
            );
        }

        return timeline;
    }

    public reset(): void {
        // @ts-ignore
        this.filters = null;

        this.counter.updateValue(`0`);

        this.scale.set(1);

        this.x = 0;
        this.y = 0;

        this.counter.reset();

        this.alpha = 1;

        this.tvFlickerSpine.reset();
    }
}
